import React from 'react';
import { Area, Bar, BarChart, CartesianGrid, ComposedChart, Line, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { isHourlyRange, type StationBucket, type StationRange, type WeatherStationOverview } from '../../../domain/WeatherStation';

// Ventana del gráfico (desde/hasta ahora) y los cortes de conexión que la
// tocan. Con esto el eje llega hasta ahora aunque la estación lleve horas
// sin reportar, y el corte se ve como una franja en vez de esconderse.
export interface ChartTimeline {
  from: number;
  to: number;
  outages: [number, number][];
}

export function timelineOf(overview: Pick<WeatherStationOverview, 'from' | 'to' | 'outages'>): ChartTimeline {
  const from = new Date(overview.from).getTime();
  const to = new Date(overview.to).getTime();
  return {
    from,
    to,
    outages: overview.outages
      .map((o): [number, number] => [Math.max(from, new Date(o.startedAt).getTime()), Math.min(to, o.endedAt ? new Date(o.endedAt).getTime() : to)])
      .filter(([a, b]) => b > a),
  };
}

// Los puntos del gráfico son tramos (de 30 min, 1 h...) y se dibujan al
// principio del tramo: la lectura de las 21:04 cae en el punto de las 21:00.
// Si el corte terminara a las 21:04, ese punto quedaría adentro de la franja
// gris. Cada corte se recorta hasta el primer punto con datos después de él.
function fitOutages(timeline: ChartTimeline | undefined, dataTs: number[]): ChartTimeline | undefined {
  if (!timeline) return timeline;
  return {
    ...timeline,
    outages: timeline.outages
      .map(([start, end]): [number, number] => {
        const resumed = dataTs.find((ts) => ts > start);
        return [start, resumed !== undefined ? Math.min(end, resumed) : end];
      })
      .filter(([a, b]) => b > a),
  };
}

// Un punto vacío al empezar cada corte, para que la línea no una el último
// dato de antes con el primero de después como si no hubiera pasado nada.
function withOutageBreaks<T extends { ts: number }>(rows: T[], timeline: ChartTimeline | undefined, empty: Omit<T, 'ts'>): T[] {
  if (!timeline || timeline.outages.length === 0) return rows;
  const breaks = timeline.outages.map(([start]) => ({ ...empty, ts: start }) as T);
  return [...rows, ...breaks].sort((a, b) => a.ts - b.ts);
}

const OUTAGE_FILL = 'var(--domiot-offline)';

// La franja lleva su nombre adentro cuando entra (más de ~1/8 del ancho).
const outageAreas = (timeline: ChartTimeline | undefined) =>
  timeline?.outages.map(([x1, x2]) => (
    <ReferenceArea
      key={x1}
      x1={x1}
      x2={x2}
      fill={OUTAGE_FILL}
      fillOpacity={0.18}
      stroke="none"
      ifOverflow="hidden"
      label={
        (x2 - x1) / (timeline.to - timeline.from) > 0.12
          ? { value: 'Sin conexión', position: 'insideTop', fill: 'var(--domiot-text-muted)', fontSize: 11 }
          : undefined
      }
    />
  ));

// Un dato sin vecinos (p. ej. el primero después de un corte) no forma
// línea: sin esto sería invisible hasta pasarle el mouse por encima.
function isolatedDot(rows: { avg: number | null }[], color: string) {
  const IsolatedDot = (props: { cx?: number; cy?: number; index?: number }) => {
    const i = props.index ?? -1;
    const alone = rows[i]?.avg != null && rows[i - 1]?.avg == null && rows[i + 1]?.avg == null;
    if (!alone || props.cx == null || props.cy == null) return <g key={i} />;
    return <circle key={i} cx={props.cx} cy={props.cy} r={4} fill={color} stroke="var(--domiot-surface)" strokeWidth={2} />;
  };
  return IsolatedDot;
}

export const OutageLegend: React.FC<{ timeline?: ChartTimeline }> = ({ timeline }) =>
  timeline && timeline.outages.length > 0 ? (
    <p className="station-chart-legend">
      <span className="swatch" />
      Sin conexión: no llegaron datos de la estación
    </p>
  ) : null;

const tooltipStyle = {
  background: 'var(--domiot-surface)',
  border: '1px solid var(--domiot-border)',
  borderRadius: 10,
  fontSize: 12,
};
const tick = { fontSize: 11, fill: 'var(--domiot-text-muted)' };

function formatTick(ts: number, range: StationRange): string {
  const d = new Date(ts);
  if (isHourlyRange(range)) return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

function formatTooltipLabel(ts: number, range: StationRange): string {
  const d = new Date(ts);
  if (isHourlyRange(range)) return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return d.toLocaleString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
}

const formatDay = (isoDate: string) => {
  const [, month, day] = isoDate.split('-');
  return `${day}/${month}`;
};

function ticksFor(rows: { ts: number }[], timeline?: ChartTimeline, count = 4): number[] {
  if (rows.length === 0) return [];
  const min = timeline?.from ?? rows[0].ts;
  const max = timeline?.to ?? rows[rows.length - 1].ts;
  if (max <= min) return [min];
  return Array.from({ length: count }, (_, i) => Math.round(min + ((max - min) * i) / (count - 1)));
}

export const ChartEmpty: React.FC<{ text?: string }> = ({ text = 'Todavía no hay datos en este período.' }) => (
  <div className="station-chart-empty">
    <p>{text}</p>
  </div>
);

// Temperatura / humedad: banda sombreada entre la mínima y la máxima de cada
// tramo, con el promedio como línea — se ve de un vistazo la amplitud y la tendencia.
export const BandChart: React.FC<{
  buckets: StationBucket[];
  color: string;
  unit: string;
  range: StationRange;
  domain?: [number, number];
  timeline?: ChartTimeline;
}> = ({ buckets, color, unit, range, domain, timeline: rawTimeline }) => {
  // Con la ventana fija hasta ahora, un solo dato ya se ubica (como punto).
  if (buckets.length < (rawTimeline ? 1 : 2)) return <ChartEmpty />;
  const data = buckets.map((b) => ({ ts: new Date(b.t).getTime(), avg: b.avg as number | null, band: [b.min, b.max] as [number, number] | null }));
  const timeline = fitOutages(rawTimeline, data.map((r) => r.ts));
  const rows = withOutageBreaks(data, timeline, { avg: null, band: null });
  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--domiot-border)" strokeDasharray="3 3" />
        {outageAreas(timeline)}
        <XAxis
          dataKey="ts"
          type="number"
          domain={timeline ? [timeline.from, timeline.to] : ['dataMin', 'dataMax']}
          ticks={ticksFor(rows, timeline)}
          tickFormatter={(ts) => formatTick(ts, range)}
          tick={tick}
          axisLine={{ stroke: 'var(--domiot-border)' }}
          tickLine={false}
        />
        <YAxis
          tick={tick}
          axisLine={false}
          tickLine={false}
          width={unit === 'hPa' ? 48 : 40}
          unit={unit === '%' ? '%' : unit === 'hPa' ? '' : '°'}
          domain={domain ?? ['auto', 'auto']}
          allowDecimals={unit !== 'hPa'}
        />
        <Tooltip
          contentStyle={tooltipStyle}
          labelFormatter={(label) => formatTooltipLabel(Number(label), range)}
          formatter={(value, name) => {
            if (name === 'band' && Array.isArray(value)) return [`${value[0]}${unit} – ${value[1]}${unit}`, 'Mín – Máx'];
            return [`${value}${unit}`, 'Promedio'];
          }}
        />
        <Area type="monotone" dataKey="band" stroke="none" fill={color} fillOpacity={0.16} isAnimationActive={false} />
        <Line type="monotone" dataKey="avg" stroke={color} strokeWidth={2.25} dot={isolatedDot(rows, color)} activeDot={{ r: 4 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// Lluvia: barras de mm (se suman, no se promedian). En 24 h van por media
// hora; en 7 y 30 días, por día calendario.
export const RainBars: React.FC<
  { range: StationRange; color: string } & ({ buckets: StationBucket[]; daily?: never } | { daily: { date: string; mm: number }[]; buckets?: never })
> = ({ range, color, buckets, daily }) => {
  const rows = daily
    ? daily.map((d) => ({ key: d.date, label: formatDay(d.date), mm: d.mm }))
    : (buckets ?? []).map((b) => {
        const ts = new Date(b.t).getTime();
        return { key: b.t, label: formatTick(ts, range), mm: b.sum };
      });
  if (rows.length === 0) return <ChartEmpty />;
  if (rows.every((r) => r.mm === 0)) return <ChartEmpty text="No llovió en este período." />;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap={2}>
        <CartesianGrid vertical={false} stroke="var(--domiot-border)" strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={tick} axisLine={{ stroke: 'var(--domiot-border)' }} tickLine={false} interval="preserveStartEnd" minTickGap={24} />
        <YAxis tick={tick} axisLine={false} tickLine={false} width={40} tickFormatter={(v: number) => `${v}mm`} allowDecimals />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--domiot-surface-alt)' }} formatter={(value) => [`${value} mm`, 'Lluvia']} />
        <Bar dataKey="mm" fill={color} radius={[3, 3, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Viento: promedio de cada tramo (línea llena) y la ráfaga máxima (punteada).
export const WindChart: React.FC<{ buckets: StationBucket[]; color: string; range: StationRange; timeline?: ChartTimeline }> = ({
  buckets,
  color,
  range,
  timeline: rawTimeline,
}) => {
  if (buckets.length < (rawTimeline ? 1 : 2)) return <ChartEmpty />;
  const data = buckets.map((b) => ({ ts: new Date(b.t).getTime(), avg: b.avg as number | null, max: b.max as number | null }));
  const timeline = fitOutages(rawTimeline, data.map((r) => r.ts));
  const rows = withOutageBreaks(data, timeline, { avg: null, max: null });
  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--domiot-border)" strokeDasharray="3 3" />
        {outageAreas(timeline)}
        <XAxis
          dataKey="ts"
          type="number"
          domain={timeline ? [timeline.from, timeline.to] : ['dataMin', 'dataMax']}
          ticks={ticksFor(rows, timeline)}
          tickFormatter={(ts) => formatTick(ts, range)}
          tick={tick}
          axisLine={{ stroke: 'var(--domiot-border)' }}
          tickLine={false}
        />
        <YAxis tick={tick} axisLine={false} tickLine={false} width={40} domain={[0, 'auto']} />
        <Tooltip
          contentStyle={tooltipStyle}
          labelFormatter={(label) => formatTooltipLabel(Number(label), range)}
          formatter={(value, name) => [`${value} km/h`, name === 'max' ? 'Ráfaga' : 'Promedio']}
        />
        <Area
          type="monotone"
          dataKey="avg"
          stroke={color}
          strokeWidth={2.25}
          fill={color}
          fillOpacity={0.14}
          isAnimationActive={false}
          dot={isolatedDot(rows, color)}
        />
        <Line type="monotone" dataKey="max" stroke={color} strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
